import { useState } from 'react';
import {
  Edit,
  Trash2,
  MoreHorizontal,
  Power,
  PowerOff,
  FileText,
  Clock,
  Calendar,
  User,
  AlertTriangle,
  Play
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingOverlay } from '@/components/LoadingSkeletons';
import { useAppStore } from '@/stores/appStore';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { formatFileNameForDisplay, getFileDisplayShort } from '@/utils/actionHelpers';
import type { Action } from '@/types';

interface ActionListProps {
  onEditAction?: (action: Action) => void;
  className?: string;
}

export function ActionList({ onEditAction, className }: ActionListProps) {
  const {
    actions: { list: actions, loading, error },
    updateAction,
    deleteAction,
    triggerAction,
    clearActionsError
  } = useAppStore();
  const { toast } = useToast();

  const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, Partial<Action>>>({});
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [triggeringIds, setTriggeringIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionToDelete, setActionToDelete] = useState<Action | null>(null);

  const handleToggleStatus = async (action: Action) => {
    const newStatus = !action.isActive;

    // Optimistic update
    setOptimisticUpdates(prev => ({
      ...prev,
      [action.id]: { isActive: newStatus }
    }));

    try {
      await updateAction(action.id, { isActive: newStatus });

      // Clear optimistic update on success
      setOptimisticUpdates(prev => {
        const { [action.id]: _, ...rest } = prev;
        return rest;
      });

      // Show success toast
      const fileDisplay = getFileDisplayShort(action.actionDetail.fileReference.fileName);

      toast({
        variant: 'default',
        title: `Action ${newStatus ? 'activated' : 'deactivated'}`,
        description: `The action for "${fileDisplay}" has been ${newStatus ? 'activated' : 'deactivated'}.`,
      });
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticUpdates(prev => {
        const { [action.id]: _, ...rest } = prev;
        return rest;
      });

      console.error('Failed to toggle action status:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        variant: 'destructive',
        title: 'Failed to update action',
        description: errorMessage,
      });
    }
  };

  const handleDeleteClick = (action: Action) => {
    setActionToDelete(action);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!actionToDelete) return;

    setDeletingIds(prev => new Set(prev).add(actionToDelete.id));
    setDeleteDialogOpen(false);

    try {
      await deleteAction(actionToDelete.id);

      const fileDisplay = getFileDisplayShort(actionToDelete.actionDetail.fileReference.fileName);

      toast({
        variant: 'default',
        title: 'Action deleted',
        description: `The action for "${fileDisplay}" has been deleted.`,
      });
    } catch (error) {
      console.error('Failed to delete action:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        variant: 'destructive',
        title: 'Failed to delete action',
        description: errorMessage,
      });
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(actionToDelete.id);
        return newSet;
      });
      setActionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setActionToDelete(null);
  };

  const handleEditAction = (action: Action) => {
    onEditAction?.(action);
  };

  const handleRunNow = async (action: Action) => {
    setTriggeringIds(prev => new Set(prev).add(action.id));

    try {
      await triggerAction(action.id);

      const fileDisplay = getFileDisplayShort(action.actionDetail.fileReference.fileName);

      toast({
        variant: 'default',
        title: 'Action triggered',
        description: `The action for "${fileDisplay}" is now running in the background.`,
      });
    } catch (error) {
      console.error('Failed to trigger action:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        variant: 'destructive',
        title: 'Failed to trigger action',
        description: errorMessage,
      });
    } finally {
      setTriggeringIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(action.id);
        return newSet;
      });
    }
  };

  const getDisplayAction = (action: Action): Action => {
    const optimisticUpdate = optimisticUpdates[action.id];
    if (optimisticUpdate) {
      return { ...action, ...optimisticUpdate };
    }
    return action;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrequencyIcon = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return <Clock className="h-3 w-3" />;
      case 'weekly':
      case 'monthly':
      case 'yearly':
        return <Calendar className="h-3 w-3" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'weekly':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'monthly':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'yearly':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-destructive mb-2">Failed to load actions</div>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={clearActionsError}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Actions ({actions.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative">
          {loading && (
            <LoadingOverlay message="Loading actions..." variant="minimal" />
          )}

          {actions.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No actions yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Create your first automated action to get started with warrant file processing.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File & Description</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actions.map((action) => {
                  const displayAction = getDisplayAction(action);
                  const isDeleting = deletingIds.has(action.id);
                  const isTriggering = triggeringIds.has(action.id);
                  const hasOptimisticUpdate = !!optimisticUpdates[action.id];

                  return (
                    <TableRow
                      key={action.id}
                      className={cn(
                        "group",
                        (isDeleting || isTriggering) && "opacity-50 pointer-events-none",
                        hasOptimisticUpdate && "bg-muted/50"
                      )}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate max-w-[300px]">
                              {formatFileNameForDisplay(displayAction.actionDetail.fileReference.fileName)}
                            </span>
                          </div>
                          {displayAction.actionDetail.metadata.description && (
                            <p className="text-sm text-muted-foreground truncate max-w-[300px]">
                              {displayAction.actionDetail.metadata.description}
                            </p>
                          )}
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            {displayAction.actionDetail.metadata.createdBy}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn(
                            "capitalize flex items-center gap-1",
                            getFrequencyColor(displayAction.frequency)
                          )}
                        >
                          {getFrequencyIcon(displayAction.frequency)}
                          {displayAction.frequency}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={displayAction.isActive ? "default" : "secondary"}
                            className={cn(
                              "flex items-center gap-1",
                              displayAction.isActive
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
                            )}
                          >
                            {displayAction.isActive ? (
                              <Power className="h-3 w-3" />
                            ) : (
                              <PowerOff className="h-3 w-3" />
                            )}
                            {displayAction.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {hasOptimisticUpdate && (
                            <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" />
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(displayAction.updatedAt)}
                        </span>
                      </TableCell>

                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              disabled={isDeleting}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open action menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => handleRunNow(action)}
                              className="cursor-pointer"
                              disabled={isTriggering}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                              onClick={() => handleEditAction(action)}
                              className="cursor-pointer"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Action
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              onClick={() => handleToggleStatus(action)}
                              className="cursor-pointer"
                            >
                              {displayAction.isActive ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(action)}
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Action
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Action
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the action for "{actionToDelete ? getFileDisplayShort(actionToDelete.actionDetail.fileReference.fileName) : ''}"?
              <br />
              <br />
              This action cannot be undone. The automated action will be permanently removed and will no longer execute on schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Action
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// Loading skeleton for ActionList
export function ActionListSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-6 w-32" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Skeleton className="h-4 w-32" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-4 w-20" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-4 w-16" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-4 w-24" />
              </TableHead>
              <TableHead className="w-[50px]">
                <Skeleton className="h-4 w-12" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                    <Skeleton className="h-3 w-32" />
                    <div className="flex items-center gap-1">
                      <Skeleton className="h-3 w-3" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 w-20 rounded-full" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-8 w-8" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}